import clsx from "clsx";
import { Icon, type IconProps } from "../icon/Icon";
import {
  CheckboxIcon,
  type CheckboxIconProps,
} from "../icon/icons/checkbox-icon/CheckboxIcon";
import styles from "./Menu.module.css";

interface MenuProps {
  size?: "sm" | "md";
  className?: string;
}

const Menu: React.FC<React.PropsWithChildren<MenuProps>> & {
  Group: typeof Group;
  Grid: typeof Grid;
  Item: typeof Item;
  Text: typeof Text;
  Title: typeof Title;
  Icon: typeof MenuIcon;
  Separator: typeof Separator;
  Header: typeof Header;
  Footer: typeof Footer;
  Checkbox: typeof Checkbox;
} = ({ size = "md", className, children }) => {
  return (
    <div className={clsx(styles.menu, styles[`menu-${size}`], className)}>
      <div className={styles["menu-scroll"]}>{children}</div>
    </div>
  );
};

interface MenuGroupProps extends React.HTMLAttributes<HTMLDivElement> {}

const Group: React.FC<MenuGroupProps> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles["menu-group"], className)} {...props}>
      {children}
    </div>
  );
};

interface MenuGridProps extends React.HTMLAttributes<HTMLDivElement> {}

const Grid: React.FC<MenuGridProps> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles["menu-grid"], className)} {...props}>
      {children}
    </div>
  );
};

interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  disabled?: boolean;
}

const Item: React.FC<React.PropsWithChildren<MenuItemProps>> = ({
  active = false,
  disabled = false,
  className,
  children,
  onClick,
  ...props
}) => {
  const itemProps = disabled === false ? { onClick } : {};

  return (
    <div
      className={clsx(
        styles["menu-item"],
        active === true && styles.active,
        disabled === true && styles.disabled,
        className,
      )}
      {...props}
      {...itemProps}
    >
      {children}
    </div>
  );
};

interface MenuTextProps extends React.HTMLAttributes<HTMLDivElement> {}

const Text: React.FC<MenuTextProps> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles["menu-text"], className)} {...props}>
      {children}
    </div>
  );
};

interface MenuTitleProps extends React.HTMLAttributes<HTMLDivElement> {}

const Title: React.FC<MenuTitleProps> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles["menu-title"], className)} {...props}>
      {children}
    </div>
  );
};

interface MenuSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {}

const Separator: React.FC<MenuSeparatorProps> = ({ className, ...props }) => {
  return (
    <div className={clsx(styles["menu-separator"], className)} {...props} />
  );
};

const MenuIcon: React.FC<IconProps> = ({ className, ...props }) => {
  return <Icon className={clsx(styles["menu-icon"], className)} {...props} />;
};

const Header: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, ...props }) => {
  return <div className={clsx(styles["menu-header"], className)} {...props} />;
};

const Footer: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, ...props }) => {
  return <div className={clsx(styles["menu-footer"], className)} {...props} />;
};

const Checkbox: React.FC<CheckboxIconProps> = ({ className, ...props }) => {
  return (
    <CheckboxIcon
      className={clsx(styles["menu-checkbox"], className)}
      {...props}
    />
  );
};

Menu.Group = Group;
Menu.Grid = Grid;
Menu.Item = Item;
Menu.Text = Text;
Menu.Title = Title;
Menu.Icon = MenuIcon;
Menu.Separator = Separator;
Menu.Header = Header;
Menu.Footer = Footer;
Menu.Checkbox = Checkbox;

export { Menu };
